import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * Reusable Alert / Confirm Dialog
 * - Default: single OK action
 * - With onConfirm: Cancel + Confirm (fancy confirm alert)
 */
const AlertDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  severity = 'info',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  okLabel = 'OK',
  confirming = false,
  container,
  disablePortal,
  sx: dialogSx,
  BackdropProps,
  PaperProps,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isConfirm = typeof onConfirm === 'function';

  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorOutlineIcon sx={{ fontSize: 34, color: '#f44336' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 34, color: '#ff9800' }} />;
      case 'success':
        return <CheckCircleOutlineIcon sx={{ fontSize: 34, color: '#4caf50' }} />;
      default:
        return <InfoIcon sx={{ fontSize: 34, color: '#4ce1b6' }} />;
    }
  };

  const getAccent = () => {
    switch (severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'success':
        return '#4caf50';
      default:
        return '#4ce1b6';
    }
  };

  const accent = getAccent();
  const confirmTextColor =
    severity === 'warning' || severity === 'error' ? '#fff' : '#04120e';

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      container={container}
      disablePortal={disablePortal}
      sx={dialogSx}
      BackdropProps={{
        ...BackdropProps,
        sx: {
          backgroundColor: alpha('#0f172a', isDark ? 0.72 : 0.45),
          backdropFilter: 'blur(6px)',
          ...(BackdropProps?.sx || {}),
        },
      }}
      PaperProps={{
        ...PaperProps,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(accent, isDark ? 0.35 : 0.22)}`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(accent, 0.12)} 0%, #0f172a 42%, #0b1220 100%)`
            : `linear-gradient(165deg, ${alpha(accent, 0.1)} 0%, #ffffff 45%, #f8fafc 100%)`,
          boxShadow: isDark
            ? `0 24px 60px ${alpha('#000', 0.55)}`
            : `0 20px 50px ${alpha('#0f172a', 0.18)}`,
          ...(PaperProps?.sx || {}),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 2.25,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: alpha(accent, isDark ? 0.18 : 0.12),
              border: `1px solid ${alpha(accent, 0.28)}`,
            }}
          >
            {getIcon()}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: 0.2 }}>
            {title || (isConfirm ? 'Confirm' : 'Alert')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={confirming}
          sx={{ color: isDark ? '#999' : '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5, pb: 1.5 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.25, gap: 1 }}>
        {isConfirm ? (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={confirming}
              sx={{
                minWidth: 100,
                borderRadius: 999,
                fontWeight: 700,
                textTransform: 'none',
                borderColor: alpha(accent, 0.45),
                color: isDark ? alpha('#fff', 0.85) : '#334155',
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={onConfirm}
              variant="contained"
              disabled={confirming}
              startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                minWidth: 120,
                borderRadius: 999,
                fontWeight: 800,
                textTransform: 'none',
                backgroundColor: accent,
                color: confirmTextColor,
                boxShadow: `0 8px 18px ${alpha(accent, 0.35)}`,
                '&:hover': { backgroundColor: accent, opacity: 0.92 },
              }}
            >
              {confirming ? 'Working…' : confirmLabel}
            </Button>
          </>
        ) : (
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: accent,
              color: confirmTextColor,
              borderRadius: 999,
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: accent,
                opacity: 0.9,
              },
              minWidth: 100,
            }}
          >
            {okLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlertDialog;
